import type {
  AccessPreview,
  AccessPreviewSourceUser,
} from 'features/permissions/types'
import {
  accessLevelOf,
  policyNameForAccess,
} from 'features/permissions/utils/policyScopeEvaluation'

const ACCESS_PREVIEW_STORAGE_KEY = 'forecast.accessPreview'

interface SerializedAccessPreview {
  targetUserId: string
  targetName: string
  targetEmail: string
  roleNames: string[]
}

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

function readStoredString(
  record: Record<string, unknown>,
  key: string
): string {
  const value = record[key]
  return typeof value === 'string' ? value : ''
}

function readStoredStringArray(
  record: Record<string, unknown>,
  key: string
): string[] {
  const value = record[key]
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

export function accessPreviewFromRolodexUser(
  user: AccessPreviewSourceUser
): AccessPreview {
  const roleNamesSet = new Set<string>()

  for (const role of user.roles ?? []) {
    roleNamesSet.add(role.name)
    for (const policy of role.policies ?? []) {
      roleNamesSet.add(policy.name)
    }
  }

  for (const scope of user.scopes ?? []) {
    roleNamesSet.add(policyNameForAccess(accessLevelOf(scope)))
  }

  return {
    targetUserId: user.id,
    targetName: user.name?.trim() || user.email || 'Unnamed user',
    targetEmail: user.email ?? '',
    roleNames: Array.from(roleNamesSet),
  }
}

export function readAccessPreviewFromStorage(): AccessPreview | null {
  const storage = sessionStorageOrNull()
  if (!storage) return null

  try {
    const raw = storage.getItem(ACCESS_PREVIEW_STORAGE_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    const record = parsed as Record<string, unknown>
    const targetUserId = record.targetUserId
    if (typeof targetUserId !== 'string') {
      return null
    }

    return {
      targetUserId,
      targetName: readStoredString(record, 'targetName'),
      targetEmail: readStoredString(record, 'targetEmail'),
      roleNames: readStoredStringArray(record, 'roleNames'),
    }
  } catch {
    return null
  }
}

export function writeAccessPreviewToStorage(preview: AccessPreview): void {
  const storage = sessionStorageOrNull()
  if (!storage) return

  try {
    const serialized: SerializedAccessPreview = {
      targetUserId: preview.targetUserId,
      targetName: preview.targetName,
      targetEmail: preview.targetEmail,
      roleNames: [...preview.roleNames],
    }
    storage.setItem(ACCESS_PREVIEW_STORAGE_KEY, JSON.stringify(serialized))
  } catch {
    return
  }
}

export function clearAccessPreviewFromStorage(): void {
  const storage = sessionStorageOrNull()
  if (!storage) return
  try {
    storage.removeItem(ACCESS_PREVIEW_STORAGE_KEY)
  } catch {
    return
  }
}

export function clearAccessPreviewIfLoggedOut(args: {
  isAuthenticated: boolean
  isLoading: boolean
}): void {
  if (args.isLoading) return
  if (!args.isAuthenticated) {
    clearAccessPreviewFromStorage()
  }
}
